/**
 * The map and compass overlays.
 *
 * Both are drawn to small canvases rather than built from DOM, because both are
 * continuous readouts that change every frame — a scrolling bearing strip and a
 * rotating position plot are far cheaper to paint than to reflow.
 *
 * The map is deliberately incomplete. It shows only towers you have physically
 * been near or that someone has activated, so it rewards exploration instead of
 * replacing it, and it never shows the monster.
 *
 * The one thing it does tell you outright is where he cannot go: the safe-zone
 * perimeter around each activated tower. That is the map's reason to exist —
 * without it the zones are invisible ground you can only discover by surviving
 * on them.
 */

import { WORLD, bearingFromYaw, cardinalFor, forwardFromYaw } from '@static-pines/shared';

export interface MapModel {
  playerX: number;
  playerZ: number;
  playerYaw: number;
  towers: Array<{ x: number; z: number; activated: boolean; known: boolean }>;
  teammates: Array<{ x: number; z: number; alive: boolean }>;
  extraction: { x: number; z: number } | null;
  spawn: { x: number; z: number };
  /**
   * Metres an activated tower keeps him out of. Passed in rather than read from
   * tuning here, because difficulty scales it and the panel has no idea which
   * tier the match is running at.
   */
  safeRadius: number;
}

const MAP_SIZE = 208;
const MAP_RADIUS = MAP_SIZE / 2 - 8;

export class MapPanel {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly scale: number;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'map-canvas';
    this.canvas.width = MAP_SIZE;
    this.canvas.height = MAP_SIZE;
    this.ctx = this.canvas.getContext('2d')!;
    this.scale = MAP_RADIUS / WORLD.PLAYABLE_RADIUS;
  }

  /** North is up. World -Z maps to screen -Y, so no rotation is applied. */
  private toScreen(x: number, z: number): [number, number] {
    return [MAP_SIZE / 2 + x * this.scale, MAP_SIZE / 2 + z * this.scale];
  }

  draw(model: MapModel): void {
    const ctx = this.ctx;
    const cx = MAP_SIZE / 2;
    const cy = MAP_SIZE / 2;

    ctx.clearRect(0, 0, MAP_SIZE, MAP_SIZE);

    // Paper.
    ctx.fillStyle = 'rgba(10, 14, 19, 0.88)';
    ctx.beginPath();
    ctx.arc(cx, cy, MAP_RADIUS + 6, 0, Math.PI * 2);
    ctx.fill();

    // The bowl rim.
    ctx.strokeStyle = 'rgba(120, 132, 146, 0.5)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(cx, cy, MAP_RADIUS, 0, Math.PI * 2);
    ctx.stroke();

    // Cardinal ticks.
    ctx.strokeStyle = 'rgba(120, 132, 146, 0.28)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 4; i++) {
      const a = (i * Math.PI) / 2;
      ctx.beginPath();
      ctx.moveTo(cx + Math.sin(a) * (MAP_RADIUS - 7), cy - Math.cos(a) * (MAP_RADIUS - 7));
      ctx.lineTo(cx + Math.sin(a) * MAP_RADIUS, cy - Math.cos(a) * MAP_RADIUS);
      ctx.stroke();
    }
    ctx.fillStyle = 'rgba(150, 160, 172, 0.7)';
    ctx.font = '9px "Courier New", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('N', cx, cy - MAP_RADIUS + 1);

    // Where you started — a useful anchor when you are lost.
    const [sx, sy] = this.toScreen(model.spawn.x, model.spawn.z);
    ctx.strokeStyle = 'rgba(140, 150, 160, 0.45)';
    ctx.beginPath();
    ctx.moveTo(sx - 3, sy);
    ctx.lineTo(sx + 3, sy);
    ctx.moveTo(sx, sy - 3);
    ctx.lineTo(sx, sy + 3);
    ctx.stroke();

    // Extraction, once it matters.
    if (model.extraction) {
      const [ex, ey] = this.toScreen(model.extraction.x, model.extraction.z);
      ctx.strokeStyle = '#5fa87a';
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.arc(ex, ey, 6, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = 'rgba(95, 168, 122, 0.28)';
      ctx.fill();
    }

    /**
     * Safe zones around activated towers.
     *
     * The one piece of genuinely strategic information on the map: ground he
     * will not enter. Drawn underneath everything else so the markers, mates
     * and your own arrow stay readable on top of it, and clipped to the paper
     * so a zone near the rim does not bleed off the edge of the disc.
     */
    const zoneRadius = model.safeRadius * this.scale;
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, MAP_RADIUS, 0, Math.PI * 2);
    ctx.clip();
    for (const tower of model.towers) {
      if (!tower.activated) continue;
      const [zx, zy] = this.toScreen(tower.x, tower.z);

      ctx.fillStyle = 'rgba(217, 201, 138, 0.1)';
      ctx.beginPath();
      ctx.arc(zx, zy, zoneRadius, 0, Math.PI * 2);
      ctx.fill();

      // Dashed, so it reads as a boundary rather than another object.
      ctx.setLineDash([3, 3]);
      ctx.strokeStyle = 'rgba(217, 201, 138, 0.45)';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.restore();

    // Towers. Unknown ones are simply absent — the map does not do your
    // exploring for you.
    for (const tower of model.towers) {
      if (!tower.known) continue;
      const [tx, ty] = this.toScreen(tower.x, tower.z);


      ctx.beginPath();
      ctx.moveTo(tx, ty - 5);
      ctx.lineTo(tx + 4.5, ty + 4);
      ctx.lineTo(tx - 4.5, ty + 4);
      ctx.closePath();

      if (tower.activated) {
        ctx.fillStyle = '#d9c98a';
        ctx.fill();
      } else {
        ctx.strokeStyle = 'rgba(217, 201, 138, 0.75)';
        ctx.lineWidth = 1.3;
        ctx.stroke();
      }
    }

    // Teammates.
    for (const mate of model.teammates) {
      const [mx, my] = this.toScreen(mate.x, mate.z);

      if (mate.alive) {
        ctx.fillStyle = 'rgba(127, 155, 176, 0.9)';
        ctx.beginPath();
        ctx.arc(mx, my, 2.6, 0, Math.PI * 2);
        ctx.fill();
        continue;
      }

      /**
       * Somebody on the floor, in red and as a cross rather than a dot.
       *
       * They used to be skipped entirely, which made a downed teammate
       * genuinely unfindable — the body is somewhere in 236 metres of
       * identical dark forest, and nothing pointed at it. The shape has to
       * differ as well as the colour: a red dot at map scale is a blue dot to
       * anyone who cannot easily tell the two apart.
       */
      ctx.strokeStyle = 'rgba(198, 88, 80, 0.95)';
      ctx.lineWidth = 1.6;
      const arm = 2.8;
      ctx.beginPath();
      ctx.moveTo(mx - arm, my - arm);
      ctx.lineTo(mx + arm, my + arm);
      ctx.moveTo(mx + arm, my - arm);
      ctx.lineTo(mx - arm, my + arm);
      ctx.stroke();
    }

    /**
     * You, as an arrow pointing where you face.
     *
     * Built from explicit vertices along the forward vector rather than by
     * rotating a fixed shape. Canvas rotation has a sign convention that is
     * easy to get backwards and hard to verify by reading, and this arrow has
     * pointed the wrong way more than once. Here the tip is literally placed
     * in the direction of travel, so there is nothing left to invert.
     *
     * Screen space is world XZ: +X is right, +Z is down, so a world forward
     * vector is already a screen direction.
     */
    const [px, py] = this.toScreen(model.playerX, model.playerZ);
    const forward = forwardFromYaw(model.playerYaw);
    // Perpendicular, for the two rear corners.
    const side = { x: -forward.z, y: forward.x };

    const tip = { x: px + forward.x * 8, y: py + forward.z * 8 };
    const rear = { x: px - forward.x * 3.5, y: py - forward.z * 3.5 };
    const notch = { x: px - forward.x * 0.5, y: py - forward.z * 0.5 };

    ctx.fillStyle = '#e2e7ee';
    ctx.beginPath();
    ctx.moveTo(tip.x, tip.y);
    ctx.lineTo(rear.x + side.x * 4.5, rear.y + side.y * 4.5);
    ctx.lineTo(notch.x, notch.y);
    ctx.lineTo(rear.x - side.x * 4.5, rear.y - side.y * 4.5);
    ctx.closePath();
    ctx.fill();

    // A short sight line ahead of the tip, so facing is unmistakable even at
    // a glance on a small canvas.
    ctx.strokeStyle = 'rgba(226, 231, 238, 0.55)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(tip.x, tip.y);
    ctx.lineTo(px + forward.x * 15, py + forward.z * 15);
    ctx.stroke();
  }
}

// ---------------------------------------------------------------------------
// Compass
// ---------------------------------------------------------------------------

const COMPASS_WIDTH = 300;
const COMPASS_HEIGHT = 30;
/** Pixels per degree of bearing on the strip. */
const PX_PER_DEG = 2.4;

export class CompassStrip {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'compass-canvas';
    this.canvas.width = COMPASS_WIDTH;
    this.canvas.height = COMPASS_HEIGHT;
    this.ctx = this.canvas.getContext('2d')!;
  }

  /**
   * @param yaw player yaw, where 0 faces +Z. North is -Z, so a yaw of 0 is
   *            due south and the bearing is offset accordingly.
   */
  draw(yaw: number): void {
    const ctx = this.ctx;
    const cx = COMPASS_WIDTH / 2;

    ctx.clearRect(0, 0, COMPASS_WIDTH, COMPASS_HEIGHT);

    const bearing = bearingFromYaw(yaw);

    ctx.font = '11px "Courier New", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Ticks every five degrees, labels on the eight cardinals.
    for (let offset = -70; offset <= 70; offset += 5) {
      const deg = (((bearing + offset) % 360) + 360) % 360;
      const x = cx + offset * PX_PER_DEG;
      if (x < 6 || x > COMPASS_WIDTH - 6) continue;

      // Fade toward the ends so the strip does not read as a hard-edged box.
      const fade = 1 - Math.min(1, Math.abs(offset) / 72);
      const cardinal = cardinalFor(deg);
      const major = cardinal !== null;

      ctx.strokeStyle = `rgba(180, 190, 202, ${(major ? 0.75 : 0.32) * fade})`;
      ctx.lineWidth = major ? 1.4 : 1;
      ctx.beginPath();
      ctx.moveTo(x, major ? 4 : 8);
      ctx.lineTo(x, major ? 13 : 12);
      ctx.stroke();

      if (cardinal) {
        ctx.fillStyle = `rgba(205, 213, 222, ${0.9 * fade})`;
        ctx.fillText(cardinal, x, 22);
      }
    }

    // Centre marker.
    ctx.strokeStyle = '#d9c98a';
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.moveTo(cx, 2);
    ctx.lineTo(cx, 15);
    ctx.stroke();
  }
}
